import React from "react";
import { View, ScrollView, TouchableOpacity } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import type { BadgeVariant } from "@/components/ui/Badge";
import { reviewApi, type ApplicationDetail, type ApplicationStatus } from "@/lib/api/review";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { PageFooter } from "@/components/ui/PageFooter";
import { Icon } from "@/components/ui/Icon";
import { Feather, FontAwesome6, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { cn } from "@/lib/utils";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  type MainTab,
  type SubTab,
  formatRelativeTime,
  formatAbsoluteDate,
  LegalRepresentativeTab,
  BusinessInformationTab,
  ProductComplianceTab,
  FinancialSettlementTab,
  BrandingLocalizationTab,
  ActivityLogTab,
  RejectionDetailsModal,
  DetailFieldReasonModal,
  DetailFieldEditModal,
} from "./components";

const INNER_TABS = [
  {
    id: "legal_representative",
    labelKey: "review.application.subtabs.legalRepresentative",
    icon: "user-circle" as const,
    iconComponent: FontAwesome6,
  },
  {
    id: "business_information",
    labelKey: "review.application.subtabs.businessInformation",
    icon: "briefcase" as const,
    iconComponent: Feather,
  },
  {
    id: "product_compliance",
    labelKey: "review.application.subtabs.productCompliance",
    icon: "shield-check-outline" as const,
    iconComponent: MaterialCommunityIcons,
  },
  {
    id: "financial_settlement",
    labelKey: "review.application.subtabs.financialSettlement",
    icon: "bank-outline" as const,
    iconComponent: MaterialCommunityIcons,
  },
  {
    id: "branding_localization",
    labelKey: "review.application.subtabs.brandingLocalization",
    icon: "color-palette-outline" as const,
    iconComponent: Ionicons,
  },
];

export default function ApplicationDetailScreen() {
  const { t, locale } = useI18n();
  const { id, tab, subtab } = useLocalSearchParams<{
    id: string;
    tab?: MainTab;
    subtab?: SubTab;
  }>();
  const router = useRouter();
  const isWideScreen = useIsWideScreen();

  const activeTab: MainTab = tab || "submitted_information";
  const activeSubTab: SubTab = subtab || "legal_representative";

  const [showRejectionDetailsModal, setShowRejectionDetailsModal] = React.useState(false);
  const [viewingDetailReasonField, setViewingDetailReasonField] = React.useState<string | null>(
    null
  );
  const [viewingDetailEditField, setViewingDetailEditField] = React.useState<{
    key: string;
    titleKey: TranslationKeys;
  } | null>(null);

  const handleShowDetailFieldReason = React.useCallback((fieldKey: string) => {
    setViewingDetailReasonField(fieldKey);
  }, []);

  const handleShowDetailFieldEdit = React.useCallback(
    (fieldKey: string, titleKey: TranslationKeys) => {
      setViewingDetailEditField({ key: fieldKey, titleKey });
    },
    []
  );

  const { data: application, isLoading } = useQuery<ApplicationDetail>({
    queryKey: ["review", "application", id],
    queryFn: () => reviewApi.getApplication(id!),
    enabled: !!id,
  });

  const queryClient = useQueryClient();
  const undoMutation = useMutation({
    mutationFn: () => reviewApi.undoDecision(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "application", id] });
      queryClient.invalidateQueries({ queryKey: ["review", "applications"] });
    },
  });

  const handleTabChange = (newTab: string) => {
    router.setParams({ tab: newTab });
  };

  const handleSubTabChange = (newSubTab: SubTab) => {
    router.setParams({ subtab: newSubTab });
  };

  if (isLoading) {
    return (
      <View className="flex-1 bg-stroke-10 items-center justify-center">
        <AppText className="text-sm text-dark-text-15">{t("common.loading")}</AppText>
      </View>
    );
  }

  const variantMap = {
    approved: "filled-success",
    pending_review: "filled-warning",
    rejected: "filled-error",
  } satisfies Record<ApplicationStatus, BadgeVariant>;

  const statusLabelMap = {
    approved: "review.application.status.approved",
    pending_review: "review.application.status.pending_review",
    rejected: "review.application.status.rejected",
  } satisfies Record<ApplicationStatus, TranslationKeys>;

  return (
    <View className="flex-1 bg-stroke-10">
      <View className="px-page-x bg-white py-4 border-b border-stroke-50">
        <Breadcrumb
          segments={[
            { labelKey: "review.dashboard.title", href: "/(staff)/(dashboard)/applications" },
            {
              labelText:
                (locale === "en"
                  ? application?.businessInformation?.companyNameEnglish
                  : locale === "ar"
                    ? application?.businessInformation?.companyNameArabic
                    : application?.businessInformation?.companyNameChinese) ??
                application?.companyName ??
                "—",
            },
          ]}
        />
      </View>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} className="bg-white">
        {/* Header */}
        <View className="bg-white pt-6">
          <View className="px-page-x w-full">
            <View className="w-full max-w-8xl self-center flex-col tablet:flex-row items-start justify-between gap-4">
              <View className="flex-1 mr-4 items-start">
                <View className="flex-row flex-wrap items-center gap-3">
                  <AppText
                    weight="bold"
                    className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
                  >
                    {(locale === "en"
                      ? application?.businessInformation?.companyNameEnglish
                      : locale === "ar"
                        ? application?.businessInformation?.companyNameArabic
                        : application?.businessInformation?.companyNameChinese) ??
                      application?.companyName ??
                      "—"}
                  </AppText>
                  {application?.status && (
                    <Badge
                      variant={variantMap[application.status]}
                      label={statusLabelMap[application.status]}
                    />
                  )}
                </View>
                {application?.submittedAt && (
                  <AppText className="text-base text-dark-text-20 mt-2">
                    {t("review.application.submittedAt")}{" "}
                    {formatRelativeTime(application.submittedAt, locale, t)} (
                    {formatAbsoluteDate(application.submittedAt, locale)})
                  </AppText>
                )}
              </View>
              {application?.status === "pending_review" ? (
                <Button
                  label="portal.dashboard.reviewApplication"
                  onPress={() => router.push(`/(staff)/applications/${id}/review`)}
                  variant="primary"
                />
              ) : (
                <View className="flex-row flex-wrap items-center gap-4">
                  {application?.status === "rejected" && (
                    <Button
                      label="review.application.rejectionDetails.viewReason"
                      onPress={() => setShowRejectionDetailsModal(true)}
                      variant="solid"
                    />
                  )}

                  <Button
                    label="review.application.undoDecision"
                    onPress={() => undoMutation.mutate()}
                    loading={undoMutation.isPending}
                    variant="tertiary"
                  />
                </View>
              )}
            </View>
          </View>

          <View className="mt-8">
            <TabBarNavigation<MainTab>
              tabs={[
                {
                  key: "submitted_information",
                  label: t("review.application.tabs.submittedInformation"),
                },
                { key: "activity_log", label: t("review.application.tabs.activityLog") },
              ]}
              activeTab={activeTab}
              onTabPress={(key) => handleTabChange(key as MainTab)}
              containerClassName="px-page-x"
              className="max-w-8xl self-center w-full"
            />
          </View>
        </View>

        <View className="flex-1 bg-white">
          {activeTab === "submitted_information" && (
            <>
              {/* Mobile: horizontal scrollable subtab strip */}
              {!isWideScreen && (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  className="bg-white border-b border-stroke-20 flex-grow-0"
                  contentContainerStyle={{ paddingHorizontal: 24, paddingVertical: 8, gap: 4 }}
                >
                  {INNER_TABS.map((tabItem) => {
                    const isActive = activeSubTab === tabItem.id;
                    return (
                      <TouchableOpacity
                        key={tabItem.id}
                        onPress={() => handleSubTabChange(tabItem.id as SubTab)}
                        className={cn(
                          "flex-row items-center gap-2 px-3 py-2 rounded-sm",
                          isActive ? "bg-primary-5" : "bg-transparent"
                        )}
                      >
                        <Icon
                          as={tabItem.iconComponent}
                          name={tabItem.icon}
                          size={16}
                          className={isActive ? "text-primary" : "text-dark-text-30"}
                        />
                        <AppText
                          weight="medium"
                          className={cn(
                            "text-sm leading-5",
                            isActive ? "text-primary" : "text-dark-text-50"
                          )}
                        >
                          {t(tabItem.labelKey as TranslationKeys)}
                        </AppText>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              )}

              <View className="px-page-x flex-1">
                <View className="w-full max-w-8xl self-center flex-1 flex-row">
                  {/* Tablet/Desktop: Left Inner Sidebar */}
                  {isWideScreen && (
                    <View className="w-60 lg:w-72 py-8 pr-6 border-r border-stroke-20 bg-white">
                      <View className="gap-2">
                        {INNER_TABS.map((tabItem) => {
                          const isActive = activeSubTab === tabItem.id;
                          return (
                            <TouchableOpacity
                              key={tabItem.id}
                              onPress={() => handleSubTabChange(tabItem.id as SubTab)}
                              className={cn(
                                "flex-row items-center gap-3 px-4 py-3 rounded-sm",
                                isActive
                                  ? "bg-primary-5 text-primary"
                                  : "bg-transparent text-dark-text-50"
                              )}
                            >
                              <Icon
                                as={tabItem.iconComponent}
                                name={tabItem.icon}
                                size={18}
                                className={isActive ? "text-primary" : "text-dark-text-30"}
                              />
                              <AppText
                                weight="medium"
                                className={cn(
                                  "text-[15px] leading-5",
                                  isActive ? "text-primary" : "text-dark-text-50"
                                )}
                              >
                                {t(tabItem.labelKey as TranslationKeys)}
                              </AppText>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>
                  )}

                  {/* Main Content Area */}
                  <View className={cn("flex-1 bg-stroke-10", isWideScreen ? "py-8 px-10" : "py-6")}>
                    {activeSubTab === "legal_representative" && (
                      <LegalRepresentativeTab
                        legalRep={application?.legalRepresentative}
                        fieldRejections={application?.fieldRejections}
                        onShowFieldReason={handleShowDetailFieldReason}
                        t={t}
                        applicationId={id}
                      />
                    )}
                    {activeSubTab === "business_information" && (
                      <BusinessInformationTab
                        bizInfo={application?.businessInformation}
                        fieldEdits={application?.fieldEdits}
                        onShowFieldEdit={handleShowDetailFieldEdit}
                        t={t}
                        applicationId={id}
                      />
                    )}
                    {activeSubTab === "product_compliance" && (
                      <ProductComplianceTab
                        compliance={application?.productCompliance}
                        t={t}
                        applicationId={id}
                      />
                    )}
                    {activeSubTab === "financial_settlement" && (
                      <FinancialSettlementTab
                        settlement={application?.financialSettlement}
                        t={t}
                        applicationId={id}
                      />
                    )}
                    {activeSubTab === "branding_localization" && (
                      <BrandingLocalizationTab
                        branding={application?.brandingLocalization}
                        fieldEdits={application?.fieldEdits}
                        onShowFieldEdit={handleShowDetailFieldEdit}
                        t={t}
                        applicationId={id}
                      />
                    )}
                    {activeSubTab !== "legal_representative" &&
                      activeSubTab !== "business_information" &&
                      activeSubTab !== "product_compliance" &&
                      activeSubTab !== "financial_settlement" &&
                      activeSubTab !== "branding_localization" && (
                        <View className="items-center justify-center py-20">
                          <EmptyState description="review.application.contentComingSoon" />
                        </View>
                      )}
                  </View>
                </View>
              </View>
            </>
          )}

          {activeTab === "activity_log" && (
            <View className="px-page-x flex-1">
              <View className="w-full max-w-8xl self-center flex-1 flex-row">
                <ActivityLogTab entries={application?.activityLog} t={t} locale={locale} />
              </View>
            </View>
          )}
        </View>

        <PageFooter />
      </ScrollView>

      <RejectionDetailsModal
        visible={showRejectionDetailsModal}
        onDismiss={() => setShowRejectionDetailsModal(false)}
        rejectedByName={application?.rejectedBy?.name}
        rejectedByRole={application?.rejectedBy?.role}
        rejectedAt={application?.rejectedAt}
        reason={application?.rejectionReason}
        t={t}
        locale={locale}
      />
      <DetailFieldReasonModal
        visible={!!viewingDetailReasonField}
        onDismiss={() => setViewingDetailReasonField(null)}
        data={
          viewingDetailReasonField
            ? application?.fieldRejections?.[viewingDetailReasonField]
            : undefined
        }
        t={t}
        locale={locale}
      />
      <DetailFieldEditModal
        visible={!!viewingDetailEditField}
        onDismiss={() => setViewingDetailEditField(null)}
        titleKey={viewingDetailEditField?.titleKey}
        data={
          viewingDetailEditField ? application?.fieldEdits?.[viewingDetailEditField.key] : undefined
        }
        t={t}
        locale={locale}
      />
    </View>
  );
}
